"use client";
import React from 'react';
import "../../scss/_privacy-policy.scss";

export default function EditorialGuidelines() {
  return (
    <div className="privacy-policy-page">
      <div className="container">
        <h1 className="pp-main-title">Editorial Guidelines</h1>
        <p className="pp-date">Updated: Nov 26, 2024</p>
        
        <h2 className="pp-section-title mt-0">About</h2>
        <p className="pp-text m-0">
          Invest in Energy is an independent platform dedicated to democratizing access to education on the energy sector. Founded by Derrick Hou, President and CEO of Eckhelm Energy Partners, and Sameer Somal, CEO of Blue Ocean Global Technology, our mission is to bridge the gap between industry expertise and investor education.
        </p>
        <p className="pp-text">
          We aim to empower both beginners and experienced investors with accessible, fact-based, and actionable insights about the energy market. Through education, transparency, and collaboration, we&apos;re building a community of ambitious, ethical, and forward-thinking energy investors.
        </p>

        <h2 className="pp-section-title">Author Application</h2>
        <p className="pp-text">
          To ensure quality and community integrity, Invest in Energy invites contributors to apply to become an Author.
        </p>
        
        <a href="#" className="pp-tertiary-title d-block mb-3" style={{textDecoration: 'none'}}>How It Works</a>
        
        <h3 className="pp-tertiary-sub-title">Application:</h3>
        <ol>
          <li>Potential Authors apply through the &quot;Become an Author&quot; form on our platform.</li>
          <li>Applicants must provide their:
            <ul>
              <li>Name</li>
              <li>Email</li>
              <li>Platform handles</li>
              <li>Affiliated website</li>
              <li>General field of commentary</li>
              <li>Professional bio</li>
            </ul>
          </li>
        </ol>

        <h3 className="pp-tertiary-sub-title">Review:</h3>
        <ol>
          <li>The editorial team reviews applications based on subject matter expertise, writing quality, and alignment with Invest in Energy&apos;s values.</li>
          <li>Applicants may be accepted or declined based on experience and content relevance.</li>
        </ol>

        <h3 className="pp-tertiary-sub-title">Approval:</h3>
        <ol>
          <li>We welcome that other peer review to the Author dashboard, where they can submit articles for review and display.</li>
          <li>Authors are subject to a trial period.</li>
        </ol>

        <h2 className="pp-section-title">Content Guidelines</h2>
        <p className="pp-text">
          Our editorial team expects that contributors comply with Invest in Energy&apos;s guidelines to ensure content is high quality.
        </p>
        <ul className="pp-head-with-text">
          <li>All information must be fact-checked and supported by reputable data sources. Avoid speculation or unverified claims.</li>
          <li>Content should simplify complex energy investment concepts without oversimplifying facts.</li>
          <li>Writers should employ a tone suitable for a broad audience, from new investors to seasoned professionals.</li>
          <li>Maintain objectivity and transparency. Avoid promotional bias, undisclosed sponsorships, or conflicts of interest.</li>
          <li>All submissions must be original and unpublished elsewhere. Plagiarism or AI-generated content without human oversight will not be accepted.</li>
          <li>Authors must not provide direct financial advice or recommend specific investments or strategies.</li>
        </ul>

        <h2 className="pp-section-title">Topics We Cover</h2>
        <p className="pp-text">
          Invest in Energy publishes resources that cover a broad spectrum of energy investment and sustainability-related themes. Below are sample topics list that we would observe:
        </p>

        <h3 className="pp-tertiary-title">Oil & Gas Fundamentals</h3>
        <ul>
          <li>Basics of upstream, midstream, downstream</li>
          <li>Understanding oil prices, supply/demand dynamics</li>
          <li>Exploration and production (E&P) processes explained</li>
          <li>Evaluating oil and gas industry investments</li>
        </ul>

        <h3 className="pp-tertiary-title">Energy Markets & Economics</h3>
        <ul>
          <li>Global and US macroeconomic trends</li>
          <li>Geopolitics and its impact on energy markets</li>
          <li>Commodity trading, futures, and options primers</li>
          <li>Impact of economic indicators on energy prices</li>
        </ul>

        <h3 className="pp-tertiary-title">Investment Strategies</h3>
        <ul>
          <li>Portfolio construction with energy assets</li>
          <li>Value vs. growth investing in energy</li>
          <li>Risk management and diversification in energy</li>
          <li>Tax-advantaged energy investments</li>
        </ul>

        <h3 className="pp-tertiary-title">Energy Transition & Innovation</h3>
        <ul>
          <li>Renewable energy investment fundamentals</li>
          <li>Clean energy tech: solar, wind, storage</li>
          <li>Regulation and policy driving the energy transition</li>
          <li>Emerging trends in energy storage and grid tech</li>
        </ul>

        <h3 className="pp-tertiary-title">Policy, ESG, and Sustainability</h3>
        <ul>
          <li>Environmental regulations and compliance</li>
          <li>Corporate governance and energy companies</li>
          <li>The role of ESG in public and private energy investing</li>
          <li>Policy incentives for renewable development</li>
        </ul>

        <h3 className="pp-tertiary-title">Thought Leadership & Expert Insight</h3>
        <ul>
          <li>Interviews with industry professionals</li>
          <li>Perspectives on commodity forecasts</li>
          <li>Analysis of energy market trends</li>
          <li>Debates on the future of global energy</li>
        </ul>

        <h2 className="pp-section-title">Categories and Workflow</h2>

        <h3 className="pp-tertiary-title">Blogs</h3>
        <p className="pp-text mb-0">
          To ensure quality, strategy, and overall integrity, we encourage short, insightful, and timely pieces on...
        </p>

        <h4 className="pp-sub-title mt-4">Guidelines:</h4>
        <ul>
          <li>Maximum of 800-1200 words.</li>
          <li>Focus on one clear takeaway or idea per blog related to the theme.</li>
          <li>Use accessible language and provide context for industry terms.</li>
          <li>Authorship is given to writers with a relevant bio.</li>
        </ul>

        <h4 className="pp-sub-title">Workflow:</h4>
        <ul>
          <li>Approved Authors submit drafts through their Author Portal on the website dashboard.</li>
          <li>Editorial team reviews submissions for clarity, relevance, and Invest in Energy standards.</li>
          <li>Edits are proposed, and the Author must confirm the final copy.</li>
          <li>The editorial team formats blog posts to include internal linking, imagery, and executive quotes.</li>
          <li>Once approved, the post will be scheduled for publication via our primary channels.</li>
        </ul>

        <h3 className="pp-tertiary-title">Articles</h3>
        <p className="pp-sub-text mb-0">
          To drive in-depth, research-driven perspectives on energy markets, technologies, policies, or investment strategies.
        </p>

        <h4 className="pp-sub-title mt-4">Guidelines:</h4>
        <ul>
          <li>Maximum of 1500-2500 words.</li>
          <li>Must include data sources and citations.</li>
          <li>Visuals (charts, graphs, tables) are highly encouraged.</li>
        </ul>

        <h4 className="pp-sub-title">Workflow:</h4>
        <ul>
          <li>Authors submit drafts through the same portal form.</li>
          <li>All Articles require a minimum double peer review process by Invest in Energy:
            <ul>
              <li>Subject matter expert review</li>
              <li>Editorial review for style/tone</li>
            </ul>
          </li>
          <li>The editor process requires:
            <ul>
              <li>Alignment of focus groups</li>
              <li>Proposal reviews</li>
              <li>Approved styling guides</li>
              <li>Editorial milestones</li>
            </ul>
          </li>
          <li>Authors are notified of formatting decisions or requested edits through...</li>
          <li>Upon approval, the article is published on the &quot;Articles&quot; page of our website.</li>
        </ul>

        <h2 className="pp-section-title">Feedback and Contact</h2>
        <p className="pp-text">
          We value collaboration and continuous improvement.
        </p>
        <p className="pp-text">
          If you have any questions, want to discuss content ideas, or wish to provide feedback on published content or the editorial process, please reach out to our editorial team at <a href="mailto:info@investinenergy.org">info@investinenergy.org</a> with the subject line &quot;Editorial Feedback – [Your Name]&quot;.
        </p>
        <p className="pp-text">
          Our team will review your message and respond within 7 business days.
        </p>
        <p className="pp-text mb-0">
          At Invest in Energy, we believe that knowledge builds community. We welcome contributors who share our vision for an accessible, informed ecosystem built on education, transparency, and integrity.
        </p>

      </div>
    </div>
  );
}
